'use client'

import { useState, useMemo } from 'react'

import { type AxiosError, type AxiosProgressEvent } from 'axios'
import _get from 'lodash/get'

import { INDEX_SLUG, LOGIN_SLUG } from '@/app/constants'
import { ApiCache, VoidApiCache } from '@/lib/cache/api-cache'
import { isDevelopmentGlammaticEnv } from '@/lib/html/env'
import { isClientSide } from '@/lib/html/server-side'
import { ensureTrailingSlash } from '@/lib/html/url'
import { getErrorCode, isForbiddenError, isUnauthorizedError } from '@/lib/http/codes'
import { Logger } from '@/lib/logger/log'
import { clearAuthCookies } from '@/lib/user/session'

import {
  clearBusinessStorage,
  clearReportStorage,
  clearUserAuthStorage,
} from '../use-storage/business'
import useStorage from '../use-storage/use-storage'
import {
  BUSINESS_STORAGE_KEY,
  USER_AUTH_STORAGE_KEY,
} from '../use-storage/use-storage.constants'

import { GlammaticApi } from './glammatic-api'
import {
  type WebsiteEvent,
  type WebsiteEventStatus,
  type PaginatedResponse,
  type Review,
  type Catalog,
  type StockImage,
  type BusinessFile,
  type CatalogCategory,
  type ReviewResponse,
  type MerchantLocation,
  type ContentSuggestion,
  type WebsiteDetail,
  type WebsitePage,
  type ProviderServiceSetting,
  type GbpAccount,
  type GbpLocation,
  type CatalogItem,
  MerchantPost,
  MerchantProduct,
  MerchantProductPromptContext,
  MerchantCategory,
  type OnboardingBlueprint,
  TeamMember,
  GbpMediaLocationAssociationCategory,
  type CheckoutResponse,
  type CheckoutSessionParams,
  type CheckoutSessionResponse,
  type QuestionGroup,
  type QuestionGroupQuestion,
  type Simulation,
  type SimulationComponent,
  type SimulationComponentReviewStatus,
  type SimulationStatus,
} from './use-glammatic-api.d'

const websiteDetailCache = new ApiCache<number, WebsiteDetail>()
const scheduleTasksCreationCronJobCache = new VoidApiCache<number>()
const provisionTasksCache = new VoidApiCache<number>()

export function useGlammaticApi() {
  const { storageLoaded: userAuthLoaded } = useStorage<UserAuth>(USER_AUTH_STORAGE_KEY)

  const { value: business, storageLoaded: businessLoaded } =
    useStorage<Business>(BUSINESS_STORAGE_KEY)
  const { id: businessId, businessInfoId, website } = business || {}
  const { id: websiteId } = website || {}

  const storageLoaded = userAuthLoaded && businessLoaded

  const [loading, setLoading] = useState(false)

  // Create API instance with memoization to prevent recreation on every render
  const api = useMemo(() => GlammaticApi.browserSession(), [])

  /**
   * Handles Axios errors with frontend-specific logic.
   * @throws {AxiosError} When the error is not unauthorized/forbidden.
   */
  async function handleError(
    error: AxiosError,
    { onPostError }: { onPostError?: (error: AxiosError) => void } = {}
  ): Promise<never | void> {
    const errorCode = getErrorCode(error as AxiosError)
    const isUnauthorized = isUnauthorizedError(error as AxiosError)
    const isForbidden = isForbiddenError(error as AxiosError)

    let currentUrl = ''
    if (isClientSide()) {
      currentUrl = window.location.href
    }
    const isLoginUrl = currentUrl.includes(`/${LOGIN_SLUG}`)

    if ((isUnauthorized || isForbidden) && !isLoginUrl) {
      Logger.debug(`Logging out due to error code: ${errorCode}`)
      await logout()
    } else {
      await api.handleError(error, { onPostError })
    }

    if (onPostError) {
      onPostError(error)
    }
  }

  // Helper function to wrap API calls with loading state
  async function withLoading<T>(
    apiCall: () => Promise<T>,
    errorHandler?: (error: AxiosError) => void
  ): Promise<T> {
    try {
      setLoading(true)
      return await apiCall()
    } catch (error) {
      if (errorHandler) {
        errorHandler(error as AxiosError)
      } else {
        await handleError(error as AxiosError)
      }
      return null as never
    } finally {
      setLoading(false)
    }
  }

  // API methods
  async function me(
    {
      forceDataRefresh = false,
      reportToSentry = true,
    }: { forceDataRefresh?: boolean; reportToSentry?: boolean } = {},
    { retries = 3 }: { retries?: number } = {}
  ): Promise<UserAuth> {
    return await withLoading(() => api.me({ forceDataRefresh }, { retries, reportToSentry }))
  }

  async function updateUser(
    userId: number,
    {
      email,
      firstName,
      lastName,
      phoneNumber,
    }: { email?: string; firstName?: string; lastName?: string; phoneNumber?: string } = {}
  ): Promise<User> {
    return await withLoading(() =>
      api.updateUser(userId, { email, firstName, lastName, phoneNumber })
    )
  }

  async function sendEmailVerification(): Promise<void> {
    return await withLoading(() => api.sendEmailVerification())
  }
  async function sendSmsVerification(): Promise<void> {
    return await withLoading(() => api.sendSmsVerification())
  }

  async function verifySmsCode(code: string): Promise<void> {
    return await withLoading(() => api.verifySmsCode(code))
  }

  async function sendPasswordResetEmail(email: string): Promise<void> {
    return await withLoading(() => api.sendPasswordResetEmail(email))
  }

  async function logout(): Promise<void> {
    function cleanSession() {
      clearBusinessStorage()
      clearReportStorage()
      clearUserAuthStorage()
    }

    const currentUrl = window.location.href
    const currentSlug = window.location.pathname

    let logoutUrl = ''
    try {
      const response = await api.logoutRequest()
      logoutUrl = response?.logoutUrl || ''
    } catch {
      // pass
    }

    // Must complete before navigating away, otherwise the browser cancels the
    // request and the auth cookies survive the logout.
    try {
      await clearAuthCookies()
    } catch {
      // pass
    }

    cleanSession()

    const isIndex = currentSlug === INDEX_SLUG || currentSlug === LOGIN_SLUG

    if (
      logoutUrl &&
      ensureTrailingSlash(logoutUrl) !== ensureTrailingSlash(currentUrl) &&
      !isIndex
    ) {
      window.location.href = logoutUrl
    }
  }

  async function getMerchantProducts(): Promise<MerchantProduct[]> {
    return await withLoading(() => api.getMerchantProducts(businessId as number))
  }

  async function getMerchantProduct(): Promise<MerchantProduct | null> {
    const products = await getMerchantProducts()
    return _get(products, '[0]', null)
  }

  async function getMerchantProductPromptContexts(
    merchantProductId: number
  ): Promise<MerchantProductPromptContext[]> {
    return await withLoading(() => api.getMerchantProductPromptContexts(merchantProductId))
  }

  async function getMerchantProductPromptContext(
    merchantProductId: number
  ): Promise<MerchantProductPromptContext | null> {
    const contexts = await getMerchantProductPromptContexts(merchantProductId)
    return _get(contexts, '[0]', null)
  }

  async function patchMerchantProductPromptContext(
    context: MerchantProductPromptContext
  ): Promise<MerchantProductPromptContext> {
    return await withLoading(() => api.patchMerchantProductPromptContext(context))
  }

  async function getMerchantCategories(): Promise<MerchantCategory[]> {
    return await withLoading(() => api.getMerchantCategories(businessId as number))
  }

  async function getOnboardingBlueprint(): Promise<OnboardingBlueprint> {
    return await withLoading(() => api.getOnboardingBlueprint(businessId as number))
  }

  async function setMerchantCategories(
    categories: Array<{ category: string; subCategory: string }>
  ): Promise<MerchantCategory[]> {
    return await withLoading(() =>
      api.setMerchantCategories(businessId as number, categories)
    )
  }

  async function getLocations(): Promise<MerchantLocation[]> {
    return await withLoading(() => api.getLocations(businessId as number))
  }

  async function getReviews(): Promise<Review[]> {
    return await withLoading(() => api.getReviews(businessId as number))
  }

  async function patchReview(reviewId: number, payload: Review): Promise<Review> {
    return await withLoading(() => api.patchReview(businessId as number, reviewId, payload))
  }

  async function patchReviewResponse(
    reviewResponseId: number,
    payload: ReviewResponse
  ): Promise<ReviewResponse> {
    return await withLoading(() => api.patchReviewResponse(reviewResponseId, payload))
  }

  async function publishReviewResponse(
    reviewResponseId: number | string
  ): Promise<ReviewResponse> {
    return await withLoading(() => api.publishReviewResponse(reviewResponseId))
  }

  async function getWebsite(): Promise<Website> {
    return await withLoading(() => api.getWebsite(websiteId as number))
  }

  async function getWebsiteDetail({
    websiteId: websiteIdParam,
    useCacheResult = false,
  }: {
    websiteId?: number
    useCacheResult?: boolean
  } = {}): Promise<WebsiteDetail> {
    const finalWebsiteId = websiteIdParam || websiteId

    return websiteDetailCache.call(
      finalWebsiteId as number,
      () => withLoading(() => api.getWebsiteDetail(finalWebsiteId as number)),
      useCacheResult
    )
  }

  async function getWebsiteEvents({
    pageSize = 40,
    status,
  }: {
    pageSize?: number
    status?: WebsiteEventStatus
  } = {}): Promise<PaginatedResponse<WebsiteEvent>> {
    return await withLoading(() =>
      api.getWebsiteEvents({
        pageSize,
        status,
        websiteId,
        businessInfoId,
      })
    )
  }

  async function patchWebsiteEvent(
    websiteEventId: number | string,
    payload: WebsiteEvent
  ): Promise<WebsiteEvent> {
    return await withLoading(() => api.patchWebsiteEvent(websiteEventId, payload))
  }

  async function getWebsiteEventFileUrl(
    websiteEventId: number | string,
    fileUuid: string
  ): Promise<{ url: string }> {
    return await withLoading(() => api.getWebsiteEventFileUrl(websiteEventId, fileUuid))
  }

  async function getWebsitePages(websiteId: number): Promise<WebsitePage[]> {
    return await withLoading(() => api.getWebsitePages(websiteId))
  }

  async function createWebsitePage(
    websiteId: number,
    payload: { title: string; slug: string; pageType: string; navigationPriority: number }
  ): Promise<WebsitePage> {
    return await withLoading(() => api.createWebsitePage(websiteId, payload))
  }

  async function getQuestionGroups(merchantProductId: number): Promise<QuestionGroup[]> {
    return await withLoading(() => api.getQuestionGroups(merchantProductId))
  }

  async function deleteQuestion(questionId: number): Promise<void> {
    return await withLoading(() => api.deleteQuestion(questionId))
  }

  async function createQuestion(
    merchantProductId: number,
    questionGroupId: number,
    data: { text: string; answers?: { text: string; isPreferred?: boolean }[] }
  ): Promise<QuestionGroupQuestion> {
    return await withLoading(() =>
      api.createQuestion(merchantProductId, questionGroupId, data)
    )
  }

  async function patchQuestion(questionId: number, payload: { text: string }): Promise<void> {
    await withLoading(() => api.patchQuestion(questionId, payload))
  }

  async function patchAnswer(answerId: number, payload: { text: string }): Promise<void> {
    await withLoading(() => api.patchAnswer(answerId, payload))
  }

  async function getBusinesses({
    search,
    pageSize = 25,
  }: {
    search?: string
    pageSize?: number
  } = {}): Promise<PaginatedResponse<Business>> {
    return await withLoading(() => api.getBusinesses({ search, pageSize }))
  }

  async function getCatalog(): Promise<Catalog> {
    return await withLoading(() => api.getCatalog(businessId as number))
  }

  async function patchCatalogCategory(
    categoryId: number,
    payload: CatalogCategory
  ): Promise<CatalogCategory> {
    return await withLoading(() =>
      api.patchCatalogCategory(businessId as number, categoryId, payload)
    )
  }

  async function patchCatalogItem(
    itemId: number,
    payload: CatalogItem
  ): Promise<CatalogItem> {
    return await withLoading(() =>
      api.patchCatalogItem(businessId as number, itemId, payload)
    )
  }

  async function getBusinessFiles(): Promise<BusinessFile[]> {
    return await withLoading(() => api.getBusinessFiles(businessId as number))
  }

  async function createBusinessFile(
    payload: {
      file: File
      filename: string
      description: string
      source: string
      fileType?: string
    },
    {
      onUploadProgress,
    }: { onUploadProgress?: (progressEvent: AxiosProgressEvent) => void } = {}
  ): Promise<BusinessFile> {
    return await withLoading(() =>
      api.createBusinessFile(businessId as number, payload, { onUploadProgress })
    )
  }

  async function getStockImages({
    query,
    page,
  }: {
    query: string
    page: number
  }): Promise<StockImage[]> {
    return await withLoading(() => api.getStockImages({ query, page }))
  }

  async function reviewContentSuggestion(
    suggestionId: number,
    {
      approved,
      reason,
      acceptedValue,
    }: { approved?: boolean; reason?: string; acceptedValue?: string }
  ): Promise<ContentSuggestion> {
    return await withLoading(() =>
      api.reviewContentSuggestion(suggestionId, { approved, reason, acceptedValue })
    )
  }

  async function getRoiReport(): Promise<RoiReport> {
    return await withLoading(() => api.getRoiReport(websiteId as number))
  }

  async function getProviderServiceSettings(): Promise<ProviderServiceSetting[]> {
    return await withLoading(() => api.getProviderServiceSettings(businessId as number))
  }

  async function getProviderServiceSetting(
    providerServiceSettingId: number
  ): Promise<ProviderServiceSetting> {
    return await withLoading(() =>
      api.getProviderServiceSetting(businessId as number, providerServiceSettingId)
    )
  }

  async function deleteProviderServiceSetting(
    providerServiceSettingId: number
  ): Promise<void> {
    return await withLoading(() =>
      api.deleteProviderServiceSetting(businessId as number, providerServiceSettingId)
    )
  }

  async function updateProviderServiceSetting(
    providerServiceSettingId: number,
    payload: ProviderServiceSetting
  ): Promise<ProviderServiceSetting> {
    return await withLoading(() =>
      api.updateProviderServiceSetting(
        businessId as number,
        providerServiceSettingId,
        payload
      )
    )
  }

  async function getTeamMembers(): Promise<TeamMember[]> {
    return await withLoading(() => api.getTeamMembers(businessId as number))
  }

  async function getGbpAuthUrl(goBackUri: string): Promise<{ url: string }> {
    return await withLoading(() => api.getGbpAuthUrl(businessId as number, goBackUri))
  }

  async function disconnectGbp(): Promise<void> {
    return await withLoading(() => api.disconnectGbp(businessId as number))
  }

  async function getGbpAccounts(): Promise<GbpAccount[]> {
    return await withLoading(() => api.getGbpAccounts(businessId as number))
  }

  async function getGbpLocations(accountId: string): Promise<GbpLocation[]> {
    return await withLoading(() => api.getGbpLocations(businessId as number, accountId))
  }

  async function associateGbpLocation(
    locations: {
      fishermanLocationId: number
      gbbAccountId: string
      gbbLocationId: string
    }[]
  ): Promise<ProviderServiceSetting[]> {
    return await withLoading(() => api.associateGbpLocation(businessId as number, locations))
  }

  async function createGbpMedia(
    providerSettingId: number,
    locationId: number,
    businessFileId: number,
    metadata: {
      locationAssociationCategory: GbpMediaLocationAssociationCategory
      description: string
    }
  ): Promise<void> {
    return await withLoading(() =>
      api.createGbpMedia(providerSettingId, locationId, businessFileId, metadata)
    )
  }

  async function scheduleTasksCreationCronJob({
    businessId: businessIdParam,
    useCacheResult = false,
  }: { businessId?: number; useCacheResult?: boolean } = {}): Promise<void> {
    const finalBusinessId = businessIdParam || businessId

    const cacheResult = useCacheResult && !isDevelopmentGlammaticEnv()

    return scheduleTasksCreationCronJobCache.call(
      finalBusinessId as number,
      () => withLoading(() => api.scheduleTasksCreationCronJob(finalBusinessId as number)),
      cacheResult
    )
  }

  async function provisionTasks({
    useCacheResult = false,
  }: { useCacheResult?: boolean } = {}): Promise<void> {
    const cacheResult = useCacheResult && !isDevelopmentGlammaticEnv()

    return provisionTasksCache.call(
      businessId as number,
      () => withLoading(() => api.provisionTasks(businessId as number)),
      cacheResult
    )
  }

  async function createPost(payload: MerchantPost): Promise<MerchantPost> {
    return await withLoading(() => api.createPost(businessId as number, payload))
  }

  async function patchPost(postId: number, payload: MerchantPost): Promise<MerchantPost> {
    return await withLoading(() => api.patchPost(businessId as number, postId, payload))
  }

  async function textCompletion(
    promptClass: string,
    params = {} as Record<string, Any>
  ): Promise<{ textCompletion: string | Record<string, Any> }> {
    return await withLoading(() =>
      api.textCompletion(businessId as number, promptClass, params)
    )
  }

  async function listSimulations({
    context,
    status,
  }: {
    context?: number
    status?: SimulationStatus | SimulationStatus[]
  } = {}): Promise<Simulation[]> {
    return await withLoading(() => api.listSimulations({ context, status }))
  }

  async function createSimulation(contextId: number): Promise<Simulation> {
    return await withLoading(() => api.createSimulation(contextId))
  }

  async function deleteSimulation(simulationId: number): Promise<void> {
    return await withLoading(() => api.deleteSimulation(simulationId))
  }

  async function approveSimulation(
    simulationId: number,
    { feedback }: { feedback?: string } = {}
  ): Promise<Simulation> {
    return await withLoading(() => api.approveSimulation(simulationId, { feedback }))
  }

  async function rejectSimulation(
    simulationId: number,
    feedback: string
  ): Promise<Simulation> {
    return await withLoading(() => api.rejectSimulation(simulationId, feedback))
  }

  async function patchSimulationComponent(
    componentId: number,
    {
      reviewStatus,
      feedback,
    }: { reviewStatus: SimulationComponentReviewStatus; feedback?: string }
  ): Promise<SimulationComponent> {
    return await withLoading(() =>
      api.patchSimulationComponent(componentId, { reviewStatus, feedback })
    )
  }

  async function regenerateWebsiteContent(targetWebsiteId: number): Promise<void> {
    return await withLoading(() => api.regenerateWebsiteContent(targetWebsiteId))
  }

  async function getCheckoutStatus(merchantUsername: string): Promise<CheckoutResponse> {
    return await withLoading(() =>
      api.get<CheckoutResponse>(`/checkout/${merchantUsername}/`)
    )
  }

  async function createCheckoutSession(
    merchantUsername: string,
    params: CheckoutSessionParams
  ): Promise<CheckoutSessionResponse> {
    return await withLoading(() =>
      api.post<CheckoutSessionResponse>(`/checkout/${merchantUsername}/session/`, params)
    )
  }

  const loaded = storageLoaded

  return {
    business,

    loaded,
    loading,

    me,
    updateUser,
    sendEmailVerification,
    sendSmsVerification,
    verifySmsCode,
    sendPasswordResetEmail,
    logout,

    getBusinesses,

    getMerchantProducts,
    getMerchantProduct,
    getMerchantProductPromptContexts,
    getMerchantProductPromptContext,
    patchMerchantProductPromptContext,
    getMerchantCategories,
    setMerchantCategories,
    getLocations,

    getBusinessFiles,
    createBusinessFile,
    getStockImages,

    getReviews,
    patchReview,
    patchReviewResponse,
    publishReviewResponse,

    getWebsite,
    getWebsiteDetail,
    getWebsiteEvents,
    patchWebsiteEvent,
    getWebsiteEventFileUrl,
    getWebsitePages,
    createWebsitePage,
    getQuestionGroups,
    deleteQuestion,
    createQuestion,
    patchQuestion,
    patchAnswer,

    getCatalog,
    patchCatalogCategory,
    patchCatalogItem,

    reviewContentSuggestion,

    getRoiReport,

    getProviderServiceSettings,
    getProviderServiceSetting,
    deleteProviderServiceSetting,
    updateProviderServiceSetting,
    associateGbpLocation,
    createGbpMedia,

    getTeamMembers,

    scheduleTasksCreationCronJob,
    provisionTasks,

    getGbpAuthUrl,
    disconnectGbp,
    getGbpAccounts,
    getGbpLocations,

    createPost,
    patchPost,

    textCompletion,

    listSimulations,
    createSimulation,
    deleteSimulation,
    approveSimulation,
    rejectSimulation,
    patchSimulationComponent,

    regenerateWebsiteContent,

    getCheckoutStatus,
    createCheckoutSession,

    getOnboardingBlueprint,
  }
}
